import React, { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { TooltipComponent, GridComponent, LegendComponent, RadarComponent } from 'echarts/components';
import { BarChart, LineChart, RadarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card, Col, Row } from 'react-bootstrap';
import { useAppContext } from '../../../providers/AppProvider';
import {
  LLM_CONTROL_LABELS,
  ML_CONTROL_KEYS,
  ML_CONTROL_LABELS,
  type AISafetyRun,
  controlScore,
  filterControlEntriesByLane,
  formatSafetyVersionTimestamp,
  readControls,
  resolveInputType,
  runHasMlPrefixedControls
} from './aiSafety.utils';

echarts.use([
  TooltipComponent,
  GridComponent,
  LegendComponent,
  RadarComponent,
  BarChart,
  LineChart,
  RadarChart,
  CanvasRenderer
]);

function humanizeControlKey(key: string): string {
  if (LLM_CONTROL_LABELS[key as keyof typeof LLM_CONTROL_LABELS]) {
    return LLM_CONTROL_LABELS[key as keyof typeof LLM_CONTROL_LABELS];
  }
  if (ML_CONTROL_LABELS[key as keyof typeof ML_CONTROL_LABELS]) {
    return ML_CONTROL_LABELS[key as keyof typeof ML_CONTROL_LABELS];
  }
  return key
    .replace(/^(llm_|ml_)/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

interface AISafetyChartsProps {
  selectedRun: AISafetyRun | null | undefined;
  allRuns: AISafetyRun[];
  lane?: 'ml' | 'llm';
}

export function AISafetyCharts({ selectedRun, allRuns, lane }: AISafetyChartsProps) {
  const { getThemeColor } = useAppContext();
  const inputType = resolveInputType(selectedRun ?? undefined);

  const dangerColor = getThemeColor('danger') || '#dc3545';
  const primaryColor = getThemeColor('primary') || '#0d6efd';
  const bodyHighlightBg = getThemeColor('body-highlight-bg') || '#ffffff';
  const borderColor = getThemeColor('border-color') || '#dee2e6';
  const lightTextEmphasis = getThemeColor('light-text-emphasis') || '#495057';

  const controlBreakdown = useMemo(() => {
    const controls = lane
      ? filterControlEntriesByLane(readControls(selectedRun ?? undefined), lane)
      : readControls(selectedRun ?? undefined);
    const entries = Object.entries(controls)
      .map(([key, val]) => ({
        key,
        label: humanizeControlKey(key),
        score: controlScore(val) ?? 0
      }))
      .filter(e => e.score > 0 || Object.keys(controls).length <= 8)
      .sort((a, b) => b.score - a.score);
    return entries;
  }, [selectedRun, lane]);

  const barOptions = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: bodyHighlightBg,
        borderColor,
        textStyle: { color: lightTextEmphasis }
      },
      grid: { left: '3%', right: '8%', bottom: '3%', containLabel: true },
      xAxis: {
        type: 'value',
        max: 100,
        name: 'Contribution to Risk Score',
        axisLabel: { color: lightTextEmphasis }
      },
      yAxis: {
        type: 'category',
        data: controlBreakdown.map(c => c.label),
        axisLabel: { color: lightTextEmphasis, width: 120, overflow: 'truncate' }
      },
      series: [
        {
          type: 'bar',
          data: controlBreakdown.map(c => ({
            value: c.score,
            itemStyle: { color: c.score >= 70 ? dangerColor : primaryColor }
          })),
          label: { show: true, position: 'right', formatter: '{c}' }
        }
      ]
    }),
    [controlBreakdown, bodyHighlightBg, borderColor, lightTextEmphasis, dangerColor, primaryColor]
  );

  const radarOptions = useMemo(() => {
    const controls = readControls(selectedRun ?? undefined);
    const mlKeys = lane === 'llm' ? [] : ML_CONTROL_KEYS.map(k => `ml_${k}`).filter(k => controls[k]);

    const indicators = mlKeys.map(k => {
      const base = k.replace(/^ml_/, '') as (typeof ML_CONTROL_KEYS)[number];
      return { name: ML_CONTROL_LABELS[base] ?? humanizeControlKey(k), max: 100 };
    });

    const values = mlKeys.map(k => controlScore(controls[k]) ?? 0);

    return {
      tooltip: { backgroundColor: bodyHighlightBg, borderColor, textStyle: { color: lightTextEmphasis } },
      radar: {
        indicator: indicators.length ? indicators : [{ name: 'No ML controls', max: 100 }],
        axisName: { color: lightTextEmphasis }
      },
      series: [
        {
          type: 'radar',
          data: [{ value: values, name: 'ML Risk Surface', areaStyle: { opacity: 0.25 } }],
          itemStyle: { color: dangerColor }
        }
      ]
    };
  }, [selectedRun, lane, bodyHighlightBg, borderColor, lightTextEmphasis, dangerColor]);

  const trendOptions = useMemo(() => {
    const sorted = [...allRuns].sort(
      (a, b) =>
        Date.parse(formatSafetyVersionTimestamp(a.timestamp) || '0') -
        Date.parse(formatSafetyVersionTimestamp(b.timestamp) || '0')
    );
    const labels = sorted.map(r => {
      const ts = formatSafetyVersionTimestamp(r.timestamp);
      if (!ts) return 'Run';
      try {
        return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit' });
      } catch {
        return ts.slice(0, 16);
      }
    });
    const scores = sorted.map(r => Number(r.risk_score ?? r.Risk_Score ?? 0));

    return {
      tooltip: {
        trigger: 'axis',
        backgroundColor: bodyHighlightBg,
        borderColor,
        textStyle: { color: lightTextEmphasis }
      },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: labels, axisLabel: { color: lightTextEmphasis, rotate: 30 } },
      yAxis: { type: 'value', max: 100, name: 'Risk Score', axisLabel: { color: lightTextEmphasis } },
      series: [
        {
          type: 'line',
          data: scores,
          smooth: true,
          itemStyle: { color: primaryColor },
          areaStyle: { opacity: 0.08 }
        }
      ]
    };
  }, [allRuns, bodyHighlightBg, borderColor, lightTextEmphasis, primaryColor]);

  const showRadar =
    lane === 'llm'
      ? false
      : runHasMlPrefixedControls(selectedRun ?? undefined) &&
        (lane === 'ml' || inputType === 'ML_APP' || inputType === 'BOTH');
  const hasControls = controlBreakdown.length > 0;
  const hasTrend = allRuns.length > 1;

  if (!hasControls && !hasTrend && !showRadar) {
    return null;
  }

  return (
    <Row className="g-3 mt-1">
      {hasControls ? (
        <Col xs={12} lg={showRadar ? 7 : 12}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h6 className="mb-1">Control Breakdown</h6>
              <p className="small text-muted mb-3">Contribution of each safety control to the overall risk score.</p>
              <ReactEChartsCore
                echarts={echarts}
                option={barOptions}
                style={{ height: Math.max(280, controlBreakdown.length * 36) }}
              />
            </Card.Body>
          </Card>
        </Col>
      ) : null}
      {showRadar ? (
        <Col xs={12} lg={hasControls ? 5 : 6}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body>
              <h6 className="mb-1">ML Risk Surface</h6>
              <p className="small text-muted mb-3">Model risk vectors across ML safety controls.</p>
              <ReactEChartsCore echarts={echarts} option={radarOptions} style={{ height: 320 }} />
            </Card.Body>
          </Card>
        </Col>
      ) : null}
      {hasTrend ? (
        <Col xs={12}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h6 className="mb-1">Risk Score Trend</h6>
              <p className="small text-muted mb-3">AI Safety risk score across evaluation versions.</p>
              <ReactEChartsCore echarts={echarts} option={trendOptions} style={{ height: 280 }} />
            </Card.Body>
          </Card>
        </Col>
      ) : null}
    </Row>
  );
}

export default AISafetyCharts;
